import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { BookOpen, Moon, Activity } from "lucide-react";
import { FAITHS, ZODIAC, SYMPTOMS } from "@/lib/lunelle-data";
import { Reveal, MaskLines, Eyebrow } from "./Reveal";
import { useProfile } from "@/lib/account";
import { useFaith } from "@/lib/useFaith";

const Card = ({ className = "", children, delay = 0, testId }) => (
  <Reveal delay={delay} className={`relative overflow-hidden rounded-[2rem] ${className}`}>
    <div data-testid={testId} className="h-full">{children}</div>
  </Reveal>
);

const FaithCard = () => {
  const [f, setF] = useState(0);
  const { member } = useProfile();
  const [myFaith] = useFaith();
  useEffect(() => {
    if (member) setF(Math.max(0, FAITHS.findIndex((x) => x.id === myFaith)));
  }, [member, myFaith]);
  const line = FAITHS[f].lines[0];
  return (
    <div className="flex h-full flex-col justify-between gap-8 bg-[#2C221E] p-7 text-[#FBF5EE] sm:p-10">
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#E8B99A]"><BookOpen className="h-4 w-4" /> Daily scripture</div>
      <motion.blockquote key={f} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
        <p className="font-serif text-3xl italic leading-snug sm:text-4xl" data-testid="faith-card-quote">"{line.text}"</p>
        <cite className="mt-4 block text-sm not-italic text-[#d9c7bd]">{line.ref}</cite>
      </motion.blockquote>
      <div className="flex flex-wrap gap-2">
        {FAITHS.map((x, i) => (
          <button key={x.id} onClick={() => setF(i)} data-testid={`faith-pill-${x.id}`}
            className={`rounded-full border px-3.5 py-1.5 text-xs font-medium transition-colors ${i === f ? "border-[#E8B99A] bg-[#E8B99A] text-[#2C221E]" : "border-white/20 text-[#F4DED3] hover:border-white/50"}`}>
            {x.name}
          </button>
        ))}
      </div>
    </div>
  );
};

const ZodiacOff = ({ onTurnOn }) => (
  <div className="flex h-full flex-col justify-between gap-6 bg-[#FBF5EE] p-7 sm:p-10" data-testid="zodiac-card-off">
    <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#9E4429]"><Moon className="h-4 w-4" /> Zodiac (optional)</div>
    <p className="font-serif text-3xl leading-tight text-[#2C221E]">Zodiac is off for you.</p>
    <p className="text-[#5C4A42]">Lunelle follows your faith first. Your style of the day comes from your cycle instead: colour, outfit, hair and beauty.</p>
    <button type="button" onClick={onTurnOn} data-testid="zodiac-card-turn-on" className="self-start rounded-full border border-[#9E4429] px-4 py-2 text-sm font-semibold text-[#9E4429] transition-colors hover:bg-[#9E4429] hover:text-[#FBF5EE]">Show zodiac anyway</button>
  </div>
);

const ZodiacCard = () => {
  const [z, setZ] = useState(4);
  const { member, zodiac: mySign, showZodiac, updateProfile } = useProfile();
  useEffect(() => {
    if (member && mySign) setZ(ZODIAC.findIndex((x) => x.id === mySign));
  }, [member, mySign]);
  if (!showZodiac) return <ZodiacOff onTurnOn={() => updateProfile({ show_zodiac: true }).catch(() => {})} />;
  const s = ZODIAC[z];
  return (
    <div className="flex h-full flex-col gap-6 bg-[#FBF5EE] p-7 sm:p-10">
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#9E4429]"><Moon className="h-4 w-4" /> Zodiac (optional)</div>
      <div className="flex items-end justify-between gap-4">
        <div>
          <motion.p key={s.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="font-serif text-4xl text-[#2C221E]" data-testid="zodiac-card-sign">{s.name}</motion.p>
          <p className="text-sm text-[#6B5A52]">{s.element} sign · {s.dates}</p>
        </div>
      </div>
      <p className="text-[#3D302B]">{s.note}</p>
      <div className="mt-auto grid grid-cols-6 gap-1.5">
        {ZODIAC.map((x, i) => (
          <button key={x.id} onClick={() => setZ(i)} data-testid={`zodiac-chip-${x.id}`} title={x.name}
            className={`rounded-lg py-1.5 text-[11px] font-semibold uppercase tracking-wider transition-colors ${i === z ? "bg-[#9E4429] text-[#FBF5EE]" : "bg-[#F4E6DC] text-[#5C4A42] hover:bg-[#EBD6C8]"}`}>
            {x.name.slice(0, 3)}
          </button>
        ))}
      </div>
    </div>
  );
};

export const Pillars = () => (
  <section id="pillars" className="relative py-28 sm:py-40" data-testid="pillars-section">
    <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
      <Reveal><Eyebrow>03 — Body, soul & sky</Eyebrow></Reveal>
      <h2 className="mt-6 max-w-4xl font-serif text-5xl font-light leading-[1] tracking-tight text-[#2C221E] sm:text-6xl lg:text-7xl">
        <MaskLines inView delay={0} lines={["Your faith. Your stars.", <em key="e" className="italic text-[#9E4429]">Your rhythm.</em>]} />
      </h2>

      <div className="mt-16 grid auto-rows-auto gap-4 lg:grid-cols-12 lg:gap-5">
        <Card className="min-h-[460px] lg:col-span-7 lg:row-span-2" testId="bento-ritual">
          <img src="/img/ritual.webp" loading="lazy" decoding="async" alt="Morning ritual with tea, journal and prayer beads" className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1.6s] hover:scale-105" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#2C221E]/80 via-[#2C221E]/10 to-transparent" />
          <div className="absolute bottom-0 p-7 sm:p-10">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#F4DED3]">The morning ritual</p>
            <p className="mt-3 max-w-md font-serif text-3xl leading-tight text-[#FBF5EE] sm:text-4xl">One quiet minute: your phase, your plate, your prayer.</p>
          </div>
        </Card>
        <Card className="lg:col-span-5" delay={0.08} testId="bento-faith"><FaithCard /></Card>
        <Card className="border border-[#C86D51]/15 lg:col-span-5" delay={0.16} testId="bento-zodiac"><ZodiacCard /></Card>
        <Card className="bg-[#9E4429] lg:col-span-5" delay={0.08} testId="bento-tracking">
          <div className="flex h-full flex-col gap-6 p-7 text-[#FBF5EE] sm:p-10">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#F4DED3]"><Activity className="h-4 w-4" /> 40+ signals tracked</div>
            <p className="font-serif text-3xl leading-tight">Every side of your cycle, not just the bleed.</p>
            <div className="mt-auto flex flex-wrap gap-2">
              {SYMPTOMS.map((s) => <span key={s} className="rounded-full bg-white/10 px-3 py-1.5 text-xs">{s}</span>)}
            </div>
          </div>
        </Card>
        <Card className="min-h-[300px] lg:col-span-7" delay={0.16} testId="bento-light">
          <img src="/img/light.webp" loading="lazy" decoding="async" alt="Woman resting in warm sunlight" className="absolute inset-0 h-full w-full object-cover object-[50%_35%]" />
          <div className="absolute inset-0 bg-[#2C221E]/30" />
          <div className="absolute bottom-0 left-0 p-7 sm:p-10">
            <p className="font-serif text-6xl text-[#FBF5EE] sm:text-7xl">6</p>
            <p className="max-w-xs text-sm text-[#FBF5EE]">faith traditions — or a secular affirmation path — written with care, never preachy.</p>
          </div>
        </Card>
      </div>
    </div>
  </section>
);
